import type { TranslationSchema } from '../pt-BR';
import type { DeepPartialStrings } from '../types';

export const goals = {
    // Stack header titles (app/goals/_layout.tsx).
    screens: {
        index: 'Goals and Pockets',
        detail: 'Goal',
        formNew: 'New goal',
        formEdit: 'Edit goal',
    },
    list: {
        emptyTitle: 'Nothing here yet',
        emptySubtitle: 'Create a goal to track an objective, or a pocket to reserve money inside an account.',
        emptyAction: 'Create',
        archivedA11y: 'Archived pockets',
        caixinhasTitle: 'Pockets',
        caixinhasSubtitle: '{{amount}} reserved in accounts',
        metasTitle: 'Goals',
        metasSubtitle: '{{amount}} tracked · without touching your balance',
        metasSubtitleEmpty: 'Without touching your balance',
        accountFallback: 'Account',
        caixinhaRemaining: '{{account}} · {{amount}} to go',
        caixinhaNoTarget: '{{account}} · no target',
        caixinhaOfTarget: 'of {{amount}}',
        caixinhaSaved: 'saved',
        goalProgress: '{{current}} of {{target}}',
        completedBadge: 'Completed',
    },
    detail: {
        moreOptionsAccessibility: 'More goal options',
        editAction: 'Edit goal',
        convertAction: 'Turn into a pocket',
        deleteAction: 'Delete goal',
        completedBadge: 'Completed',
        remainingLabel: 'Remaining',
        predictionLabel: 'Forecast',
        avgPerMonthLabel: 'Avg/month',
        // months_zero: a goal at 100% that hasn't been completed yields a
        // forecast of 0 — today's screen shows "0 months" (pt-BR would treat 0 as _one).
        months_zero: '{{count}} months',
        months_one: '{{count}} month',
        months_other: '{{count}} months',
        deadlineLabel: 'Deadline',
        historyTitle: 'History',
        seeLess: 'See less',
        seeAll: 'See all',
        emptyTitle: 'No deposits yet.',
        emptySubtitle: 'Start investing in your goal!',
        depositedLabel: 'Deposited',
        depositDateWithNotes: '{{date}} · {{notes}}',
        reopenButton: 'Reopen',
        completeButton: 'Complete',
        depositButton: 'Deposit',
    },
    depositSheet: {
        title: 'New deposit',
        amountLabel: 'Amount',
        notesLabel: 'Note (optional)',
        notesPlaceholder: 'e.g. March salary',
        helper: "A deposit only marks goal progress — it doesn't touch any account balance.",
        confirmButton: 'Confirm deposit',
    },
    deleteDialog: {
        title: 'Delete goal',
        message: 'Are you sure? Deposits recorded in the goal will be removed. No transaction or account balance is changed.',
        confirm: 'Delete',
    },
    removeDepositDialog: {
        title: 'Remove deposit',
        message: 'Remove this deposit from the goal history? Progress will be adjusted. No account or transaction is affected.',
        confirm: 'Remove',
    },
    form: {
        heroLabelCaixinha: 'Pocket name',
        heroLabelMeta: 'Goal name',
        heroLabelNewCaixinha: 'New pocket',
        heroLabelNewMeta: 'New goal',
        heroLabelConvert: 'Turn into a pocket',
        namePlaceholder: 'e.g. Vacation, Emergency fund',
        accountLabelOptional: 'Account (optional)',
        accountLabel: 'Account',
        accountFallback: 'Account',
        accountLocked: '{{account}} — locked (delete and recreate to change)',
        accountNone: 'No account — becomes a goal',
        accountChoose: 'Choose account',
        targetLabelOptional: 'Target amount (optional)',
        targetLabel: 'Target amount',
        initialLabel: "How much you've already saved (optional)",
        initialSourceAccount: 'Already in the balance',
        initialSourceApart: 'A separate amount',
        initialHelperNoAccount: 'Reserves part of the account balance right at creation.',
        initialHelperAvailable: 'Available in {{account}}: {{amount}}.',
        initialHelperApart: 'Adds {{amount}} to the {{account}} balance and reserves it. Your available stays {{available}}.',
        initialHelperClamped: 'Only {{available}} fits in the balance; reserves {{available}} and you add the rest later.',
        initialHelperClampedConvert: 'Only {{available}} fits in the balance. To bring it all, choose “A separate amount” or lower the value.',
        initialHelperFromBalance: 'Reserves from the {{account}} balance: available drops from {{from}} to {{to}}.',
        emergencyLabel: 'Emergency fund',
        guaranteedLabel: 'Guaranteed limit',
        guaranteedNone: 'Link to a card (optional) — the saved amount adds to its limit',
        guaranteedLinked: 'Adds to the available limit of {{card}}',
        guaranteedClear: 'Remove card link',
        guaranteedSelectorTitle: 'Guaranteed limit card',
        emergencyDescription: 'Counts as a safety cushion in your financial health score.',
        deadlineLabel: 'Deadline (optional)',
        colorLabel: 'Color',
        iconLabel: 'Icon',
        noteCaixinhaWithAccount: "Reserves part of the {{account}} balance: Available drops, but the money stays in the account — it's not a transfer or an expense.",
        noteCaixinha: "Reserves part of the balance: Available drops, but the money stays in the account — it's not a transfer or an expense.",
        noteMeta: "A goal is tracking only: it doesn't reserve money or touch any account balance.",
        noteConvert: 'Pick the account where the money is. What the goal already has becomes the saved amount, and the goal leaves the list.',
        convertedToast: 'The goal is now a pocket',
        convertShortfall: 'Available only covers {{available}}. Choose “A separate amount” or lower the value; the goal stays as it is.',
        convertGoalMissing: 'This goal no longer exists.',
        saveA11yCreate: 'Create',
        saveA11ySave: 'Save',
        accountSelectorClear: 'No account (becomes a goal)',
        accountSelectorClearHint: 'Just tracks the goal, without setting money aside',
        accountSelectorTitle: 'Pocket account',
        colorSheetTitle: 'Choose a color',
        iconSheetTitle: 'Choose an icon',
        clampToast: {
            title: 'Saved what fit',
            message: 'Reserved {{amount}} — you add the rest in the pocket.',
        },
    },
} satisfies DeepPartialStrings<TranslationSchema['goals']>;
